import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { moneyLogsApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import FilterBar from '../../components/shared/FilterBar'
import DetailDrawer from '../../components/shared/DetailDrawer'
import JsonViewer from '../../components/shared/JsonViewer'
import Pagination from '../../components/shared/Pagination'
import StatusBadge from '../../components/shared/StatusBadge'
import MoneyCell from '../../components/shared/MoneyCell'
import ErrorState from '../../components/shared/ErrorState'
import { formatDatetime, moneyLogTypeLabel, formatMoney } from '../../lib/utils'
import type { MoneyLog } from '../../types'

import { tx } from '../../lib/i18n'
const typeOptions = [
  { label: tx('Subscription Payment'), value: 'subscription_payment' },
  { label: tx('Refund'), value: 'refund' },
  { label: tx('Credit Adjustment'), value: 'credit_adjustment' },
  { label: tx('Debit Adjustment'), value: 'debit_adjustment' },
  { label: tx('Failed Payment'), value: 'failed_payment' },
  { label: tx('Manual Charge'), value: 'manual_charge' },
  { label: tx('Wallet Credit'), value: 'wallet_credit' },
  { label: tx('Wallet Debit'), value: 'wallet_debit' },
]

const typeStatusMap: Record<string, string> = {
  subscription_payment: 'active',
  refund: 'refunded',
  credit_adjustment: 'active',
  debit_adjustment: 'past_due',
  failed_payment: 'failed',
  manual_charge: 'pending',
  wallet_credit: 'active',
  wallet_debit: 'past_due',
}

const MoneyLogsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [selectedLog, setSelectedLog] = useState<MoneyLog | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['money-logs', { page, search, typeFilter }],
    queryFn: () =>
      moneyLogsApi
        .list({ page, limit: 20, search, type: typeFilter || undefined })
        .then((r) => r.data),
  })

  const logs = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const columns: Column<MoneyLog>[] = [
    { key: 'createdAt', header: tx('Date'), sortable: true, render: (r) => <span className="text-gray-500 text-sm">{formatDatetime(r.createdAt)}</span> },
    { key: 'shopName', header: tx('Shop'), sortable: true, render: (r) => <span className="font-medium text-gray-900">{r.shopName}</span> },
    { key: 'type', header: tx('Type'), render: (r) => <StatusBadge status={typeStatusMap[r.type] ?? 'active'} label={moneyLogTypeLabel(r.type)} /> },
    { key: 'amount', header: tx('Amount'), render: (r) => <MoneyCell amount={r.amount} colorize showSign /> },
    { key: 'balanceBefore', header: tx('Before'), render: (r) => <MoneyCell amount={r.balanceBefore} /> },
    { key: 'balanceAfter', header: tx('After'), render: (r) => <MoneyCell amount={r.balanceAfter} /> },
    { key: 'actorAdmin', header: tx('Actor'), render: (r) => <span className="text-gray-500 text-sm">{r.actorAdmin ?? <em className="text-gray-300">{tx('System')}</em>}</span> },
    { key: 'source', header: tx('Source'), render: (r) => <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{r.source ?? '—'}</span> },
  ]

  if (error) return <ErrorState message={tx('Failed to load money logs.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Money Logs')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Complete financial transaction ledger')}</p>
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: tx('Search by shop name...') }}
        filters={[{ key: 'type', label: tx('Type'), options: typeOptions, value: typeFilter, onChange: setTypeFilter }]}
        onReset={() => { setSearch(''); setTypeFilter(''); setPage(1) }}
      />

      <div>
        <DataTable columns={columns} data={logs} isLoading={isLoading} emptyMessage={tx('No money logs found.')} onRowClick={(row) => setSelectedLog(row)} />
        <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
      </div>

      <DetailDrawer open={!!selectedLog} onClose={() => setSelectedLog(null)} title={tx('Transaction Detail')} subtitle={selectedLog ? moneyLogTypeLabel(selectedLog.type) : undefined} width="md">
        {selectedLog && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: tx('Shop'), value: selectedLog.shopName },
                { label: tx('Type'), value: <StatusBadge status={typeStatusMap[selectedLog.type] ?? 'active'} label={moneyLogTypeLabel(selectedLog.type)} /> },
                { label: tx('Amount'), value: <MoneyCell amount={selectedLog.amount} colorize showSign /> },
                { label: tx('Currency'), value: selectedLog.currency },
                { label: tx('Balance Before'), value: selectedLog.balanceBefore == null ? '—' : formatMoney(selectedLog.balanceBefore, selectedLog.currency) },
                { label: tx('Balance After'), value: selectedLog.balanceAfter == null ? '—' : formatMoney(selectedLog.balanceAfter, selectedLog.currency) },
                { label: tx('Actor'), value: selectedLog.actorAdmin ?? tx('System') },
                { label: tx('Source'), value: <span className="font-mono text-xs">{selectedLog.source ?? '—'}</span> },
                { label: tx('Date'), value: formatDatetime(selectedLog.createdAt) },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-400">{label}</p>
                  <div className="mt-1 text-sm font-medium text-gray-800">{value}</div>
                </div>
              ))}
            </div>
            {selectedLog.description && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-700 mb-1">{tx('Description')}</p>
                <p className="text-sm text-blue-800">{selectedLog.description}</p>
              </div>
            )}
            {selectedLog.metadata && <JsonViewer data={selectedLog.metadata} label={tx('Metadata')} />}
          </div>
        )}
      </DetailDrawer>
    </div>
  )
}

export default MoneyLogsPage
